import api from '@/js/services/axios'
import OrganizationRepository from '../OrganizationRepository'
import i18n from '@/i18n'

// Payment gateway codes — mirror asco.eventapi.features.payment.PaymentGateway on the backend.
// Use these everywhere FE code compares against an active gateway or sends a gateway code to the API.
export const PAYMENT_GATEWAY_STRIPE = 'stripe'
export const PAYMENT_GATEWAY_KLIX = 'klix'
export const PAYMENT_GATEWAY_MAKECOMMERCE = 'makecommerce'

/**
 * Every supported gateway, in the order the settings page offers them. Iterate this instead of
 * writing conditions over pairs — `gateway === STRIPE ? a : b` reads correctly with two gateways and
 * silently hands the third one Klix's branch (see the no-implicit-fallback rule in CLAUDE.md).
 *
 * The order is a product decision, not an accident: the Baltic providers come first and Stripe last.
 * Our studios are Latvian, MakeCommerce and Klix are the ones they are likely to already hold an
 * account with, and the first card is the one most trainers will take. Keep this list and the
 * provider cards in `PaymentGatewayEditor` in the same order.
 */
export const PAYMENT_GATEWAYS = [PAYMENT_GATEWAY_MAKECOMMERCE, PAYMENT_GATEWAY_KLIX, PAYMENT_GATEWAY_STRIPE]

/**
 * MakeCommerce environments — mirror asco.eventapi.integration.makecommerce.MakeCommerceEnvironment.
 * Unlike Stripe, MakeCommerce test and live are separate shops, so this is detected from the
 * credentials when they are saved and reported back by the status endpoint; the trainer never picks it.
 */
export const MAKECOMMERCE_ENVIRONMENT_TEST = 'test'
export const MAKECOMMERCE_ENVIRONMENT_LIVE = 'live'

/**
 * How each gateway is named to a trainer. Brand names, so they are not translated — the point is
 * that the trainer recognises where a link will take them.
 */
export const PAYMENT_GATEWAY_DISPLAY_NAME = {
  [PAYMENT_GATEWAY_STRIPE]: 'Stripe',
  [PAYMENT_GATEWAY_KLIX]: 'Klix',
  [PAYMENT_GATEWAY_MAKECOMMERCE]: 'MakeCommerce'
}

/**
 * Where a studio signs up for its own MakeCommerce merchant account. The `affiliate` parameter is
 * what tells MakeCommerce the studio came from us, which is the condition for the commission terms
 * they offer ASport studios — so it is never dropped from this URL.
 */
const MAKECOMMERCE_SIGNUP_URL = 'https://merchant.maksekeskus.ee/sign-up.html?locale={locale}&affiliate=ASport'

/**
 * The sign-up page's own locales, per app locale. MakeCommerce serve the Baltic languages; Russian is
 * not among them, so a Russian-speaking trainer is sent to the English page rather than to a URL that
 * may not resolve. Keyed by every locale the app supports, so adding one fails here rather than
 * silently falling through to a default nobody chose.
 */
const MAKECOMMERCE_SIGNUP_LOCALE = {
  lv: 'lv',
  en: 'en',
  ru: 'en'
}

/**
 * The MakeCommerce sign-up link for this app locale.
 *
 * @param {string} appLocale one of the app's locales (lv, en, ru)
 * @returns {string} absolute URL
 * @throws {Error} on a locale this has no mapping for — a wrong sign-up link costs the studio the
 *         affiliate terms, so an unknown locale is a bug to fix rather than something to guess at
 */
export function getMakeCommerceSignupUrl (appLocale) {
  const signupLocale = MAKECOMMERCE_SIGNUP_LOCALE[appLocale]
  if (!signupLocale) {
    throw new Error('No MakeCommerce sign-up locale mapped for app locale: ' + appLocale)
  }
  return MAKECOMMERCE_SIGNUP_URL.replace('{locale}', signupLocale)
}

/**
 * Where a studio signs up for its own Klix merchant account.
 *
 * `utm_source` is **attribution only** — it tells Klix the studio came from us and nothing more.
 * Unlike MakeCommerce's `affiliate` parameter it carries no commission terms, so losing it costs a
 * statistic rather than a studio's pricing. Do not copy the MakeCommerce comment onto it.
 */
export const KLIX_SIGNUP_URL = 'https://klix.app/?utm_source=ASport'

/** The MakeCommerce merchant portal, where the trainer copies the Shop ID and secret key from. */
export const MAKECOMMERCE_PORTAL_URL = 'https://merchant.maksekeskus.ee/'

/**
 * Welcome wizard progress, as saved in `organization.wizard_step` — mirrors
 * asco.eventapi.domain.model.ROrganization.WizardStep (`STEP_1`…`STEP_6`, `COMPLETED`).
 * `STEP_n` names the step to show next; the wizard owns which step each number means.
 */
export const WIZARD_STEP_COMPLETED = 'COMPLETED'
const WIZARD_STEP_PATTERN = /^STEP_(\d+)$/

export function toWizardStep (stepNumber) {
  return `STEP_${stepNumber}`
}

/**
 * @param {string} wizardStep a saved `STEP_n` value
 * @returns {number} n
 * @throws {Error} on any other value — the backend enum allows nothing else, so it is a bug, not a state to guess at
 */
export function parseWizardStepNumber (wizardStep) {
  const match = WIZARD_STEP_PATTERN.exec(wizardStep)
  if (!match) {
    throw new Error('Unknown wizard_step: ' + wizardStep)
  }
  return Number(match[1])
}

export default {
  LAYOUT_STYLE_CARD: OrganizationRepository.LAYOUT_STYLE_CARD,
  LAYOUT_STYLE_WEEK: OrganizationRepository.LAYOUT_STYLE_WEEK,

  // Layout options with descriptions and icons for UI components
  LAYOUT_OPTIONS: [
    { 
      value: OrganizationRepository.LAYOUT_STYLE_WEEK, 
      text: i18n.t('mypages.common.constants.layout.week'),
      description: i18n.t('mypages.common.constants.layout.week-description'),
      icon: 'mdi-calendar-week',
      color: '#4CAF50'
    },
    { 
      value: OrganizationRepository.LAYOUT_STYLE_CARD, 
      text: i18n.t('mypages.common.constants.layout.card'),
      description: i18n.t('mypages.common.constants.layout.card-description'),
      icon: 'mdi-view-grid',
      color: '#1976D2'
    },
  ],

  /**
   * @param {number|null} [organizationId] - When provided (e.g. after login), used for this request so store is not required yet.
   * When omitted, org id is taken from store (dashboard/activeOrganizationId).
   */
  get (organizationId) {
    const config = organizationId != null ? { organizationId } : {}
    return new Promise((resolve, reject) => {
      api.get('private/v1/org/{orgId}', config)
        .then(response => {
          var data = response.data
          resolve(this._processOrganization(data))
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  
  updateOrganization (myOrganization){
    return new Promise((resolve, reject) => {
      // Add is_patch to the organization object without modifying the original
      const payload = {
        ...myOrganization,
        is_patch: true
      };
      
      api.patch('private/v1/org/{orgId}', payload)
        .then(response => {
          var data = response.data
          resolve(this._processOrganization(data))
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  updateWizard (wizardData) {
    return new Promise((resolve, reject) => {
      const payload = {
        is_patch: true,
        ...wizardData
      };
      
      api.patch('private/v1/org/{orgId}/wizard', payload)
        .then(response => {
          var data = response.data
          resolve(this._processOrganization(data))
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  /**
   * Close account (soft delete). Requires account name for confirmation.
   * @param {number} organizationId - org id (uses store if not provided)
   * @param {string} accountName - exact account name for confirmation
   */
  closeAccount (organizationId, accountName) {
    return api.post('private/v1/org/{orgId}/close', { account_name: accountName }, { organizationId })
  },

  /**
   * Get Stripe Connect account link URL for onboarding. Redirect user to response.url.
   * @param {string} returnUrl - URL to redirect after onboarding (e.g. current settings page with ?stripe=return)
   * @param {string} refreshUrl - URL if link expired
   * @returns {Promise<{ url: string, accountId?: string }>}
   */
  getStripeConnectAccountLink (returnUrl, refreshUrl) {
    const params = new URLSearchParams({ return_url: returnUrl, refresh_url: refreshUrl })
    return api.get(`private/v1/org/{orgId}/stripe-connect/account-link?${params}`)
      .then(res => res.data)
  },

  /**
   * Get Stripe Connect status for the active organization.
   * @returns {Promise<{ connected: boolean, configured: boolean, detailsSubmitted?: boolean, chargesEnabled?: boolean, accountId?: string }>}
   */
  getStripeConnectStatus () {
    return api.get('private/v1/org/{orgId}/stripe-connect/status')
      .then(res => res.data)
  },

  /**
   * Disconnect Stripe Connect account from the organization.
   * @returns {Promise<Object>} Updated organization (same shape as get()).
   */
  stripeConnectDisconnect () {
    return api.post('private/v1/org/{orgId}/stripe-connect/disconnect')
      .then(res => this._processOrganization(res.data))
  },

  /**
   * Save the organization's Klix merchant credentials. Secret is write-only (never returned).
   * @param {string} brandId - Klix Brand ID
   * @param {string} secretKey - Klix Secret Key
   * @returns {Promise<Object>} Updated organization (same shape as get()).
   */
  saveKlixCredentials (brandId, secretKey) {
    return api.post('private/v1/org/{orgId}/klix/credentials', { brand_id: brandId, secret_key: secretKey })
      .then(res => this._processOrganization(res.data))
  },

  /**
   * Get Klix configuration status for the active organization.
   * @returns {Promise<{ configured: boolean, brandId?: string }>}
   */
  getKlixStatus () {
    return api.get('private/v1/org/{orgId}/klix/status')
      .then(res => res.data)
  },

  /**
   * Clear Klix credentials and disable online payments (mirrors Stripe disconnect).
   * @returns {Promise<Object>} Updated organization (same shape as get()).
   */
  klixDisconnect () {
    return api.post('private/v1/org/{orgId}/klix/disconnect')
      .then(res => this._processOrganization(res.data))
  },

  /**
   * Save the organization's MakeCommerce merchant credentials. Secret is write-only (never returned).
   *
   * The API also detects which MakeCommerce environment the credentials belong to (test shops and
   * live shops are separate shops on separate hosts) and refuses the connection while the studio's
   * Rekvizīti are incomplete — MakeCommerce requires a merchant to be identifiable to its customers.
   * @param {string} shopId - MakeCommerce Shop ID
   * @param {string} secretKey - MakeCommerce Secret Key
   * @returns {Promise<Object>} Updated organization (same shape as get()).
   */
  saveMakeCommerceCredentials (shopId, secretKey) {
    return api.post('private/v1/org/{orgId}/makecommerce/credentials', { shop_id: shopId, secret_key: secretKey })
      .then(res => this._processOrganization(res.data))
  },

  /**
   * Get MakeCommerce configuration status for the active organization.
   * @returns {Promise<{ configured: boolean, shopId?: string, environment?: string, missingStudioDetails: string[] }>}
   */
  getMakeCommerceStatus () {
    return api.get('private/v1/org/{orgId}/makecommerce/status')
      .then(res => res.data)
  },

  /**
   * Clear MakeCommerce credentials and disable online payments (mirrors Stripe/Klix disconnect).
   * @returns {Promise<Object>} Updated organization (same shape as get()).
   */
  makeCommerceDisconnect () {
    return api.post('private/v1/org/{orgId}/makecommerce/disconnect')
      .then(res => this._processOrganization(res.data))
  },

  /**
   * Select the organization's payment gateway. Rejected by API if the chosen gateway
   * is not configured (Stripe not connected / Klix or MakeCommerce credentials missing).
   * @param {(typeof PAYMENT_GATEWAY_STRIPE | typeof PAYMENT_GATEWAY_KLIX | typeof PAYMENT_GATEWAY_MAKECOMMERCE)} gateway
   * @returns {Promise<Object>} Updated organization (same shape as get()).
   */
  setPaymentGateway (gateway) {
    return api.post('private/v1/org/{orgId}/payment-gateway', { gateway })
      .then(res => this._processOrganization(res.data))
  },

  _processOrganization(organization){
    if (!organization){
      return null
    }

    organization = OrganizationRepository._processOrganization(organization)

    return organization
  }
}
